import axios from 'axios';
import Env from '@/environment';
import store from '@/store';
import GlobalFunctions from './global';

export default {
  /**
   * Call to unoapp API that creates a new folder.
   * @function
   * @param {number} businessId - The id of the business where the folder is being created.
   * @param {object} folder - The folder to be created.
   * @param {integer} parentFolderId - The folder id of the new folders parent.
   * @returns {object} A promise that will return either a success object or an error object.
   * @version 0.0.8
   */
  createFolder ({
    businessId,
    folder,
    parentFolderId
  }) {
    return new Promise((resolve, reject) => {
    
      const options = {
        method: 'PUT',
        url: '/resources/business_folders',
        body: {
          name: folder.name,
          parent_id: parentFolderId,
          business_id: businessId,
          is_shared: folder.is_shared
        },
        success (response) {
          resolve(response)
        },
        error (e) {
          reject(e)
        }
      }
      GlobalFunctions.$ajax(options, 'resources')
    })
  },

  /**
   * Create url to upload file
   * @param {number} businessId - The id of the business where the file is being uploaded.
   * @param {number} folderId - The id of the folder where the file is being uploaded.
   * @param {integer} contentType - The file type
   */
  createPreSignedUrl(businessId, folderId, contentType = 'image/jpeg') {
    const url = `${Env.resource_url}/resources/pre_signed_url/`;
    const data = {
      business_id: businessId,
      folder_id: folderId,
      content_type: contentType,
    };
    return axios({
      method: 'PUT',
      url,
      headers: {
        Authorization: `Bearer ${store.getters.getLoggedInUserToken}`,
        'Access-Control-Allow-Methods': '*',
      },
      data,
    });
  },

  /**
   * Upload assets
   * @param {number} url - Aws url to upload file
   */
  saveFileToAWS(url, file, contentType, uploadFileRef) {
    return axios({
      method: 'PUT',
      url,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      data: uploadFileRef,
    });
  },

  /**
   * Save file to database
   */

  saveFileTodatabase(data) {
    const url = `${Env.resource_url}/resources/save_file`;
    return axios({
      method: 'POST',
      url,
      data,
    });
  },

  /**
   * 
   * @param {*} businessId - The id of the business where the file is being uploaded.
   * @param {*} folderId - The id of the folder where the file is being uploaded.
   * @param {*} contentType - File content type
   */
  addFileOnAws(businessId, folderId, file, contentType, uploadFileRef) {

    return this.createPreSignedUrl(businessId, folderId, contentType)
      .then(res => res)
      .then((res2) => {
        return this.saveFileToAWS(res2.data.payload.pre_signed_url, file, contentType, uploadFileRef)
          .then(() => {
            return this.saveFileTodatabase({
              business_id: businessId,
              folder_id: folderId,
              file_path: res2.data.payload.file_path,
            });
          })
          .catch(err => err);
      })
      .catch(err => err);
  },

  /**
   * Call to unoapp API that uploads a file to a business resource folder.
   * @function
   * @param {number} businessId - The id of the business where the file is being uploaded.
   * @param {number} folderId - The id of the folder where the file is being uploaded.
   * @param {integer} file - The file to be uploaded
   * @returns {object} A promise that will return either a success object or an error object.
   */
  createFile({
    businessId,
    folderId,
    file,
    contentType,
    uploadFileRef,
  }) {
    return this.addFileOnAws(businessId, folderId, file, contentType, uploadFileRef)
      .then(response => response)
      .catch(err => err);
  },

  /**
   * Call to unoapp API that gets folders within a parent folder.
   * @function
   * @param {integer} businessId - The id of the business where the folder is being pulled from.
   * @param {integer} parentFolderId - The id of the parent folder being pulled from.
   * @param {integer} pageNumber - The `page` number being pulled in.
   * @param {integer} recordsPerPage - The max number of records to pull in.
   * @returns {object} A promise that will return either a success object or an error object.
   */
  getFolders ({ businessId, parentFolderId, pageNumber = 1, recordsPerPage = 25 }) {
    return new Promise((resolve, reject) => {
      const options = {
        method: 'POST',
        url: '/resources/search/business_folders',
        body: {
          business_id: businessId,
          parent_folder_id: parentFolderId,
          page_number: pageNumber,
          records_per_page: recordsPerPage,
          sort_by: 'name',
          sort_order: 'asc'
        },
        success: function (response) {
          resolve(response)
        },
        error: function (error) {
          reject(error)
        }
      }
      GlobalFunctions.$ajax(options, 'resources')
    })
  },

  /**
   * Call to unoapp API that returns an array of files for the specified page and search criteria.
   * @function
   * @param {integer} businessId - The id of the business where the resources are being fetched.
   * @param {integer} parentFolderId - The id of the folder where the resouces are being fetched.
   * @param {integer} pageNumber - The current page that we are retrieving results for.
   * @param {integer} recordsPerPage - The number of records to return per page.
   * @param {string} sortOrder - 'asc' or 'desc'.
   * @param {string} sortBy - The field to sort by.
   * @param {object} searchCriteria - Object with all criteria to be searched by.
   * @param {object} sortCriteria - The field and order to be sorted by.
   * @returns {object} A promise that will return either a success object or an error object.
   */
  getFiles ({
    businessId,
    parentFolderId,
    pageNumber = 1,
    recordsPerPage = 25,
    sortOrder = 'asc',
    sortBy = 'name',
    searchCriteria = [],
    sortCriteria = []
  }) {
    let httpParams = {
      business_id: businessId,
      folder_id: parentFolderId,
      page_number: pageNumber,
      records_per_page: recordsPerPage,
      sort_by: sortBy,
      sort_order: sortOrder
    }

    for (let sortKey in sortCriteria) {
      httpParams[sortKey] = sortCriteria[sortKey]
    }

    for (let searchKey in searchCriteria) {
      httpParams[searchKey] = searchCriteria[searchKey]
    }

    return new Promise((resolve, reject) => {
      const options = {
        method: 'POST',
        url: '/resources/search/business_files',
        body: httpParams,
        success (response) {
          resolve(response)
        },
        error (e) {
          reject(e)
        }
      }
      GlobalFunctions.$ajax(options, 'resources')
    })
  },

  /**
   * Call to unoapp API that updates an existing folder.
   * @function
   * @param {integer} businessId - The id of the business where the folder is being created.
   * @param {integer} folderId - The id of the folder being modified.
   * @param {string} folderName - The name of the folder being modified.
   * @returns {object} A promise that will return either a success object or an error object.
   */
  saveFolder ({ businessId, folderId, folderName }) {
    return new Promise((resolve, reject) => {
      const options = {
        method: 'PATCH',
        url: '/resources/business_folders',
        body: {
          business_id: businessId,
          folder_id: folderId,
          name: folderName,
          is_shared: 1
        },
        success (response) {
          resolve(response)
        },
        error (e) {
          reject(e)
        }
      }
      GlobalFunctions.$ajax(options, 'resources')
    })
  },

  /**
   * Call to unoapp API that updates an existing resource.
   * @function
   * @param {integer} businessId - The id of the business where the resource is being updated.
   * @param {integer} locationId - The id of the location where the resource is being updated.
   * @param {integer} resourceId - The id of the resource to be updated.
   * @param {integer} resourceName - The updated name of the resource.
   * @returns {object} A promise that will return either a success object or an error object.
   */
  saveFile ({ businessId, locationId, resourceId, resourceName }) {
    return new Promise((resolve, reject) => {
      const options = {
        method: 'PATCH',
        url: '/resources/business_file',
        body: {
          file_id: resourceId,
          name: resourceName
        },
        success (response) {
          resolve(response)
        },
        error (e) {
          reject(e)
        }
      }
      GlobalFunctions.$ajax(options, 'resources')
    })
  },

  /**
   * Call to unoapp API that deletes a folder
   * @function
   * @param {integer} businessId - The id of the business where the resource is being deleted.
   * @param {integer} folderId - The id of the resource to be deleted.
   * @returns {object} A promise that will return either a success object or an error object.
   */
  deleteFolder ({ businessId, folderId }) {
    return new Promise((resolve, reject) => {
      const options = {
        method: 'DELETE',
        url: '/resources/business_folders',
        body: {
          folder_id: folderId,
          business_id: businessId
        },
        processbody: false,
        success (response) {
          resolve(response)
        },
        error (e) {
          reject(e)
        }
      }
      GlobalFunctions.$ajax(options, 'resources')
    })
  },

  /**
   * Call to unoapp API that deletes a file
   * @function
   * @param {integer} businessId - The id of the business where the resource is being deleted.
   * @param {integer} resourceId - The id of the resource to be deleted.
   * @returns {object} A promise that will return either a success object or an error object.
   */
  deleteFile ({ businessId, fileId }) {
    return new Promise((resolve, reject) => {
      const options = {
        method: 'DELETE',
        url: '/resources/business_file',
        body: {
          file_id: fileId,
          business_id: businessId
        },
        processbody: false,
        success (response) {
          resolve(response)
        },
        error (e) {
          reject(e)
        }
      }
      GlobalFunctions.$ajax(options, 'resources')
    })
  }
}
